(() => {
  const SELECTOR = '[data-series-hub]';

  const initialiseHub = (root) => {
    if (!root || root.dataset.seriesHubReady === 'true') return;
    root.dataset.seriesHubReady = 'true';

    const grid = root.querySelector('[data-series-grid]');
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll('[data-series-card]'));
    const resultCount = root.querySelector('[data-series-result-count]');
    const emptyState = root.querySelector('[data-series-empty]');
    const sortSelect = root.querySelector('[data-series-sort]');
    const rangeInputs = Array.from(root.querySelectorAll('[data-series-range]'));
    const resetButton = root.querySelector('[data-series-reset]');
    const showResults = root.querySelector('[data-series-show-results]');
    const filterFooter = root.querySelector('[data-series-filter-footer]');
    const drawer = root.querySelector('[data-series-filter]');
    const openButton = root.querySelector('[data-series-filter-open]');
    const closeButtons = Array.from(root.querySelectorAll('[data-series-filter-close]'));
    const viewButtons = Array.from(root.querySelectorAll('[data-hub-view]'));
    const viewPanels = Array.from(root.querySelectorAll('[data-hub-panel]'));
    const collator = new Intl.Collator(document.documentElement.lang || 'en', {
      sensitivity: 'base',
      numeric: true,
    });

    let lastFocusedElement = null;
    let closeTimer = null;
    let openFrame = null;
    let drawerOpenRequested = false;

    const labelForCount = (count) => (
      count === 1 ? root.dataset.seriesLabelOne : root.dataset.seriesLabelOther
    );

    const matchesSelectedRange = (card, selectedRanges) => {
      if (!selectedRanges.length) return true;
      const count = Number(card.dataset.seriesCount || 0);
      return selectedRanges.some((range) => {
        if (range === '1-4') return count >= 1 && count <= 4;
        if (range === '5-12') return count >= 5 && count <= 12;
        if (range === '13-plus') return count >= 13;
        return true;
      });
    };

    const compareCards = (first, second, sortValue) => {
      const firstOrder = Number(first.dataset.seriesOrder || 0);
      const secondOrder = Number(second.dataset.seriesOrder || 0);
      const firstCount = Number(first.dataset.seriesCount || 0);
      const secondCount = Number(second.dataset.seriesCount || 0);
      const firstTitle = first.dataset.seriesTitle || '';
      const secondTitle = second.dataset.seriesTitle || '';

      if (sortValue === 'title-ascending') return collator.compare(firstTitle, secondTitle);
      if (sortValue === 'title-descending') return collator.compare(secondTitle, firstTitle);
      if (sortValue === 'count-descending') return secondCount - firstCount || firstOrder - secondOrder;
      if (sortValue === 'count-ascending') return firstCount - secondCount || firstOrder - secondOrder;
      return firstOrder - secondOrder;
    };

    const updateCount = (count) => {
      if (resultCount) resultCount.textContent = `${count} ${labelForCount(count)}`;
      if (showResults) {
        showResults.textContent = (root.dataset.showResultsTemplate || '__count__')
          .replaceAll('__count__', String(count));
      }
      if (emptyState) emptyState.hidden = count !== 0;
    };

    const applyState = () => {
      const selectedRanges = rangeInputs.filter((input) => input.checked).map((input) => input.value);
      const sortValue = sortSelect?.value || 'featured';
      const orderedCards = [...cards].sort((first, second) => compareCards(first, second, sortValue));
      let visibleCount = 0;

      orderedCards.forEach((card) => {
        const visible = matchesSelectedRange(card, selectedRanges);
        card.hidden = !visible;
        if (visible) visibleCount += 1;
        grid.append(card);
      });

      updateCount(visibleCount);
    };

    const updateFilterFooter = () => {
      if (filterFooter) filterFooter.hidden = !rangeInputs.some((input) => input.checked);
    };

    const focusableElements = () => {
      if (!drawer) return [];
      return Array.from(drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
      )).filter((element) => !element.hidden && element.getClientRects().length > 0);
    };

    const closeDrawer = ({ restoreFocus = true } = {}) => {
      if (!drawer || drawer.hidden) return;
      window.clearTimeout(closeTimer);
      if (openFrame) window.cancelAnimationFrame(openFrame);
      openFrame = null;
      drawerOpenRequested = false;
      drawer.classList.remove('is-open');
      openButton?.setAttribute('aria-expanded', 'false');

      const delay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 520;
      closeTimer = window.setTimeout(() => {
        drawer.classList.remove('is-open');
        drawer.hidden = true;
        document.body.classList.remove('series-filter-open');
        if (restoreFocus && lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus();
      }, delay);
    };

    const openDrawer = () => {
      if (!drawer) return;
      window.clearTimeout(closeTimer);
      if (openFrame) window.cancelAnimationFrame(openFrame);
      drawerOpenRequested = true;
      lastFocusedElement = document.activeElement;
      drawer.hidden = false;
      drawer.classList.remove('is-open');
      openButton?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('series-filter-open');
      void drawer.offsetWidth;
      openFrame = requestAnimationFrame(() => {
        openFrame = null;
        if (!drawerOpenRequested || drawer.hidden) return;
        drawer.classList.add('is-open');
        const closeButton = drawer.querySelector('.series-filter__close');
        closeButton?.focus();
      });
    };

    const handleDrawerKeydown = (event) => {
      if (!drawer || drawer.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = focusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const setActiveView = (view, { focus = false } = {}) => {
      const activeButton = viewButtons.find((button) => button.dataset.hubView === view);
      const activePanel = viewPanels.find((panel) => panel.dataset.hubPanel === view);
      if (!activeButton || !activePanel) return;

      viewButtons.forEach((button) => {
        const isActive = button === activeButton;
        button.setAttribute('aria-selected', String(isActive));
        button.tabIndex = isActive ? 0 : -1;
      });

      viewPanels.forEach((panel) => {
        panel.hidden = panel !== activePanel;
      });

      if (view !== 'series') closeDrawer({ restoreFocus: false });
      if (focus) activeButton.focus();
    };

    const handleViewKeydown = (event) => {
      const currentIndex = viewButtons.indexOf(event.currentTarget);
      if (currentIndex < 0) return;

      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % viewButtons.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + viewButtons.length) % viewButtons.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = viewButtons.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      setActiveView(viewButtons[nextIndex].dataset.hubView, { focus: true });
    };

    sortSelect?.addEventListener('change', applyState);
    rangeInputs.forEach((input) => input.addEventListener('change', () => {
      applyState();
      updateFilterFooter();
    }));
    resetButton?.addEventListener('click', () => {
      if (sortSelect) sortSelect.value = 'featured';
      rangeInputs.forEach((input) => { input.checked = false; });
      applyState();
      updateFilterFooter();
    });
    openButton?.addEventListener('click', openDrawer);
    closeButtons.forEach((button) => button.addEventListener('click', () => closeDrawer()));
    drawer?.addEventListener('keydown', handleDrawerKeydown);
    viewButtons.forEach((button) => {
      button.addEventListener('click', () => setActiveView(button.dataset.hubView));
      button.addEventListener('keydown', handleViewKeydown);
    });

    root.addEventListener('shopify:block:select', (event) => {
      const selectedCard = event.target?.querySelector?.('[data-series-card]');
      if (selectedCard) {
        setActiveView('series');
        selectedCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });

    const requestedPanelId = window.location.hash.slice(1);
    const requestedButton = viewButtons.find(
      (button) => button.getAttribute('aria-controls') === requestedPanelId,
    );
    const selectedButton = requestedButton
      || viewButtons.find((button) => button.getAttribute('aria-selected') === 'true');

    setActiveView(selectedButton?.dataset.hubView || 'series');
    applyState();
    updateFilterFooter();
  };

  const initialiseAll = (scope = document) => {
    if (scope.matches?.(SELECTOR)) initialiseHub(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(initialiseHub);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll());
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
